import { type ReactElement } from 'react';
import { ShieldCheck, UserCheck, UserX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DataTableHeaderRow,
  DataTablePill,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';

import type { TenantUserViewModel } from '../api';

const ACTIVE_STATUSES: ReadonlySet<string> = new Set(['ACTIVE', 'active', 'Active']);

/**
 * Tenant-scoped user table (PTV2-1426 tenant-admin reach). Mirrors `AdminUsersTable`'s layout and
 * density, but swaps the single "delete" action for activate/deactivate + "Manage roles" — the
 * tenant-admin reach never hard-deletes a user record.
 */
export function TenantUsersTable({
  users,
  emptyCell,
  onActivate,
  onDeactivate,
  onManageRoles,
  pendingUserGuid,
}: {
  readonly users: readonly TenantUserViewModel[];
  readonly emptyCell: string;
  readonly onActivate: (user: TenantUserViewModel) => void;
  readonly onDeactivate: (user: TenantUserViewModel) => void;
  readonly onManageRoles: (user: TenantUserViewModel) => void;
  /** guid of a user with an in-flight activate/deactivate mutation. */
  readonly pendingUserGuid: string | null;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <DataTableTable aria-label={t('routes.platform.detail.users.table.ariaLabel')}>
      <DataTableThead className="sticky top-0 z-10 bg-card">
        <DataTableHeaderRow>
          <DataTableTh widthClassName="w-[18%] min-w-[7rem]">
            {t('routes.platform.detail.users.table.username')}
          </DataTableTh>
          <DataTableTh widthClassName="w-[16%] min-w-[8rem]">
            {t('routes.platform.detail.users.table.fullName')}
          </DataTableTh>
          <DataTableTh widthClassName="w-[24%] min-w-[10rem]">
            {t('routes.platform.detail.users.table.email')}
          </DataTableTh>
          <DataTableTh widthClassName="w-[20%] min-w-[9rem]">
            {t('routes.platform.detail.users.table.roles')}
          </DataTableTh>
          <DataTableTh widthClassName="w-[10%] min-w-[5rem]">
            {t('routes.platform.detail.users.table.status')}
          </DataTableTh>
          <DataTableTh widthClassName="w-[12%]" align="right">
            <span className="sr-only">{t('routes.platform.detail.users.table.actions')}</span>
          </DataTableTh>
        </DataTableHeaderRow>
      </DataTableThead>
      <DataTableTbody>
        {users.map((u) => {
          const displayName = u.fullName.length > 0
            ? u.fullName
            : u.name.length > 0
              ? u.name
              : u.email;
          const isActive = u.status !== null && ACTIVE_STATUSES.has(u.status);
          const isPending = pendingUserGuid === u.uuid;
          return (
            <DataTableTr key={u.uuid}>
              <DataTableTd className="font-medium text-foreground" dataOffset={false}>
                <span className="truncate">{u.name.length > 0 ? u.name : emptyCell}</span>
              </DataTableTd>
              <DataTableTd dataOffset>{u.fullName.length > 0 ? u.fullName : emptyCell}</DataTableTd>
              <DataTableTd className="truncate" dataOffset>
                {u.email.length > 0 ? u.email : emptyCell}
              </DataTableTd>
              <DataTableTd dataOffset>
                {u.roles.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {u.roles.map((role) => (
                      <DataTablePill key={role}>{role}</DataTablePill>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">{emptyCell}</span>
                )}
              </DataTableTd>
              <DataTableTd dataOffset>
                {u.status !== null ? (
                  isActive ? (
                    <DataTablePill>{u.status}</DataTablePill>
                  ) : (
                    <DataTablePill className="border-muted-foreground/30 bg-muted text-muted-foreground">
                      {u.status}
                    </DataTablePill>
                  )
                ) : (
                  <span className="text-xs text-muted-foreground">{emptyCell}</span>
                )}
              </DataTableTd>
              <DataTableTd align="right" dataOffset>
                <div className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      onManageRoles(u);
                    }}
                    aria-label={t('routes.platform.detail.users.manageRolesAction', { name: displayName })}
                  >
                    <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  {isActive ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      disabled={isPending}
                      onClick={() => {
                        onDeactivate(u);
                      }}
                      aria-label={t('routes.platform.detail.users.deactivateAction', { name: displayName })}
                    >
                      <UserX className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      disabled={isPending}
                      onClick={() => {
                        onActivate(u);
                      }}
                      aria-label={t('routes.platform.detail.users.activateAction', { name: displayName })}
                    >
                      <UserCheck className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  )}
                </div>
              </DataTableTd>
            </DataTableTr>
          );
        })}
      </DataTableTbody>
    </DataTableTable>
  );
}
